// client/src/components/dashboard/properties/AddPropertyForm.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave } from 'react-icons/fa'; // Added FaSave
import countriesList from '../../../utils/countries'; // Assuming path is correct
import { addProperty } from '../../../api/authApi';

import './AddPropertyForm.css'; // Assuming CSS file exists


// --- Define Category -> Type Relationship ---
// Keys should match translation file structure (e.g., properties.category.residential, properties.type.house)
const propertyHierarchy = {
    residential: [
        { value: 'house', labelKey: 'properties.type.house', defaultLabel: 'House' },
        { value: 'apartment', labelKey: 'properties.type.apartment', defaultLabel: 'Apartment' },
        { value: 'room', labelKey: 'properties.type.room', defaultLabel: 'Room' },
        { value: 'other_residential', labelKey: 'properties.type.other_residential', defaultLabel: 'Other Residential' }
    ],
    agricultural: [
        { value: 'farm', labelKey: 'properties.type.farm', defaultLabel: 'Farm' },
        { value: 'field', labelKey: 'properties.type.field', defaultLabel: 'Field' },
        { value: 'barn', labelKey: 'properties.type.barn', defaultLabel: 'Barn' },
        { value: 'greenhouse', labelKey: 'properties.type.greenhouse', defaultLabel: 'Greenhouse' },
        { value: 'other_agricultural', labelKey: 'properties.type.other_agricultural', defaultLabel: 'Other Agricultural' }
    ],
    commercial: [
        { value: 'office', labelKey: 'properties.type.office', defaultLabel: 'Office' },
        { value: 'shop', labelKey: 'properties.type.shop', defaultLabel: 'Shop' },
        { value: 'warehouse', labelKey: 'properties.type.warehouse', defaultLabel: 'Warehouse' },
        { value: 'other_commercial', labelKey: 'properties.type.other_commercial', defaultLabel: 'Other Commercial' }
    ],
    industrial: [
        { value: 'factory', labelKey: 'properties.type.factory', defaultLabel: 'Factory' },
        { value: 'workshop', labelKey: 'properties.type.workshop', defaultLabel: 'Workshop' },
        { value: 'other_industrial', labelKey: 'properties.type.other_industrial', defaultLabel: 'Other Industrial' }
    ],
    outdoor: [ // Example category
        { value: 'garden', labelKey: 'properties.type.garden', defaultLabel: 'Garden' },
        { value: 'garage', labelKey: 'properties.type.garage', defaultLabel: 'Garage' },
        { value: 'pool_area', labelKey: 'properties.type.pool_area', defaultLabel: 'Pool Area' },
        { value: 'other_outdoor', labelKey: 'properties.type.other_outdoor', defaultLabel: 'Other Outdoor' }
    ],
    other: [
        { value: 'other', labelKey: 'properties.type.other', defaultLabel: 'Other' }
    ]
};

// Define categories for the first dropdown
const categoryOptions = [
    { value: 'residential', labelKey: 'properties.category.residential', defaultLabel: 'Residential' },
    { value: 'agricultural', labelKey: 'properties.category.agricultural', defaultLabel: 'Agricultural' },
    { value: 'commercial', labelKey: 'properties.category.commercial', defaultLabel: 'Commercial' },
    { value: 'industrial', labelKey: 'properties.category.industrial', defaultLabel: 'Industrial' },
    { value: 'outdoor', labelKey: 'properties.category.outdoor', defaultLabel: 'Outdoor/Utility' },
    { value: 'other', labelKey: 'properties.category.other', defaultLabel: 'Other' },
];
// --- End Data Structures ---

// Helper function to capitalize words in a name
const capitalizeName = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

function AddPropertyForm({ onSuccess, onCancel, existingProperties }) { // Added onCancel prop
    const { t } = useTranslation();
    const [propertyName, setPropertyName] = useState('');
    const [propertyCategory, setPropertyCategory] = useState('');
    const [propertyType, setPropertyType] = useState('');
    const [filteredTypeOptions, setFilteredTypeOptions] = useState([]); // Types available

    // Optional address fields state
    const [addressStreet, setAddressStreet] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [addressPostalCode, setAddressPostalCode] = useState('');
    const [addressCountry, setAddressCountry] = useState('');

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Handle Category Change - Updates filtered types and resets type
    const handleCategoryChange = (event) => {
        const newCategory = event.target.value;
        setPropertyCategory(newCategory);
        setPropertyType(''); // Reset type when category changes
        setFilteredTypeOptions(propertyHierarchy[newCategory] || []);
        setError('');
    };

    // Generic handler for other inputs
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setError(''); // Clear error on any input change

        switch (name) {
            case 'propertyName': setPropertyName(value); break;
            case 'propertyType': setPropertyType(value); break;
            case 'addressStreet': setAddressStreet(value); break;
            case 'addressCity': setAddressCity(value); break;
            case 'addressState': setAddressState(value); break;
            case 'addressPostalCode': setAddressPostalCode(value); break;
            case 'addressCountry': setAddressCountry(value); break;
            default: break;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!propertyName) {
            setError(t('properties.error.nameRequired', 'Property Name is required.')); // Add key
            return;
        }

        if (propertyName.length > 12) {
            setError(t('properties.error.nameTooLong', 'Property Name cannot exceed 12 characters.')); // Add key
            return;
       }

        // --- Format Name ---
        const formattedName = capitalizeName(propertyName);
        
        // Check if a property with the same name (case-insensitive) already exists in the list passed from parent
        if (existingProperties && existingProperties.some(prop => prop.property_name.toLowerCase() === formattedName.toLowerCase())) {
            setError(t('properties.error.nameExists', 'You already have a property with this name.')); // Add key
            return; // Stop submission
        }

        setLoading(true);
        try {
            const payload = {
                property_name: formattedName, // Use formatted name
                property_type: propertyType || null,
                property_category: propertyCategory || null,
                address_street: addressStreet.trim() || null,
                address_city: addressCity.trim() || null,
                address_state: addressState.trim() || null,
                address_postal_code: addressPostalCode.trim() || null,
                address_country: addressCountry || null,
            };
            console.log("Attempting to add property with payload:", payload);
            await addProperty(payload); // Use mock/real API
            onSuccess(); // Call parent callback (closes form, refreshes list)
            // No need to reset form here, as the component will unmount/be hidden
        } catch (err) {
            console.error("Error adding property:", err);
            setError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-property-form">
            <h2>{t('properties.addFormTitle', 'Add New Property')}</h2>
            {error && <div className="form-error">{error}</div>}
            <form onSubmit={handleSubmit}>
                <div className="form-row-3-column">
                    {/* Property Name (Required) */}
                    <div className="form-group">
                        <label htmlFor="propertyName">{t('properties.nameLabel', 'Property Name')}:*</label>
                        <input type="text" id="propertyName" name="propertyName" value={propertyName} onChange={handleInputChange} maxLength={12} required />
                    </div>

                    {/* Property Category Select (Optional) - Moved Up */}
                    <div className="form-group">
                        <label htmlFor="propertyCategory">{t('properties.categoryLabel', 'Property Category')}:</label>
                        <select id="propertyCategory" name="propertyCategory" value={propertyCategory} onChange={handleCategoryChange}>
                            <option value="">{t('properties.selectCategory', 'Select Category (Optional)')}</option>
                            {categoryOptions.map(opt => (<option key={opt.value} value={opt.value}>{t(opt.labelKey, opt.defaultLabel)}</option>))}
                        </select>
                    </div>

                    {/* Property Type Select (Optional & Dependent) - Moved Down */}
                    <div className="form-group">
                        <label htmlFor="propertyType">{t('properties.typeLabel', 'Property Type')}:</label>
                        <select
                            id="propertyType"
                            name="propertyType"
                            value={propertyType}
                            onChange={handleInputChange}
                            disabled={!propertyCategory || filteredTypeOptions.length === 0} // Disable if no category or no types for category
                        >
                            <option value="">{t('properties.selectType', 'Select Type (Optional)')}</option>
                            {/* Map over FILTERED type options */}
                            {filteredTypeOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{t(opt.labelKey, opt.defaultLabel)}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {/* Optional Address Fields */}
                <div className="form-group">
                    <label htmlFor="addressStreet">{t('properties.addressStreetLabel', 'Street Address (Optional)')}:</label>
                    <input type="text" id="addressStreet" name="addressStreet" value={addressStreet} onChange={handleInputChange} />
                </div>
                {/* Use form-row for address part if desired */}
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="addressCity">{t('properties.addressCityLabel', 'City (Optional)')}:</label>
                        <input type="text" id="addressCity" name="addressCity" value={addressCity} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="addressState">{t('properties.addressStateLabel', 'State/Province (Optional)')}:</label>
                        <input type="text" id="addressState" name="addressState" value={addressState} onChange={handleInputChange} />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="addressPostalCode">{t('properties.addressPostalCodeLabel', 'Postal Code (Optional)')}:</label>
                        <input type="text" id="addressPostalCode" name="addressPostalCode" value={addressPostalCode} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                        <label htmlFor="addressCountry">{t('properties.addressCountryLabel', 'Country (Optional)')}:</label>
                        <select id="addressCountry" name="addressCountry" value={addressCountry} onChange={handleInputChange} >
                            <option value="">{t('dashboard.account.contact.selectCountry')}</option> {/* Reusing key */}
                            {countriesList.map(country => (<option key={country.value} value={country.value}>{t(`countries.${country.value}`, country.label)}</option>))}
                        </select>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="form-actions">
                    <button type="submit" className="form-action-button save-button" disabled={loading}>
                        {loading ? t('common.saving', 'Saving...') : <><FaSave /> {t('common.save', 'Save Property')}</>} {/* Changed icon/text */}
                    </button>
                    {/* Cancel button calls onCancel prop */}
                    <button type="button" className="form-action-button cancel-button" onClick={onCancel} disabled={loading}>
                        {t('common.cancel', 'Cancel')}
                    </button>
                </div>
            </form>
        </div>
    );
}

// Note: This file should likely only export AddPropertyForm
// The ManageProperties component logic (fetching list, toggling form) should be
// in client/src/components/dashboard/properties/ManageProperties.js
export default AddPropertyForm;